import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { DesktopAppService } from './desktop-app.service';
import type { IdleResolvedEvent } from '@intranet/modules/desktop/providers/visarc-desktop.global';

/**
 * Subscribes to "user just returned from being idle" events forwarded by the
 * Electron preload (`window.visarcDesktop.onIdleResolved`) and re-emits them
 * inside Angular's zone.
 *
 * Outside the Visarc Timer Electron shell `window.visarcDesktop` is undefined
 * and this service stays silent — `idleResolved$` never emits — so the website
 * is completely unaffected.
 *
 * The subscriber (currently `IdleResolutionModalComponent`) decides whether
 * to act: by default we only prompt the user when a timer is actually running
 * — there's nothing to reconcile if no timer is going.
 */
@Injectable({ providedIn: 'root' })
export class IdleMonitorService implements OnDestroy {
    private _idleResolved$ = new Subject<IdleResolvedEvent>();
    private unsubscribe: (() => void) | null = null;

    readonly idleResolved$: Observable<IdleResolvedEvent> = this._idleResolved$.asObservable();

    constructor(
        private zone: NgZone,
        desktopApp: DesktopAppService,
    ) {
        if (!desktopApp.isTimerApp) return;
        const api = typeof window !== 'undefined' ? window.visarcDesktop : undefined;
        if (!api) return;

        // IPC events arrive outside Angular's zone; wrap with zone.run() so
        // change detection picks up state changes (modal open / close, etc.).
        this.unsubscribe = api.onIdleResolved(event => {
            this.zone.run(() => this._idleResolved$.next(event));
        });
    }

    ngOnDestroy(): void {
        this.unsubscribe?.();
        this.unsubscribe = null;
    }
}
